import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import api from '../../lib/axios';
import {
  LayoutDashboard,
  FolderKanban,
  Wallet,
  FileText,
  MessageSquare,
  MessageCircle,
  Bookmark,
  Users,
  ChevronDown,
  User,
  FileSignature,
  UserPlus,
  HandCoins,
  Image,
  BookOpen,
  Rocket,
  Calendar,
  Receipt,
  Euro,
  Key,
  Clock,
  Settings,
  Code,
  Shield,
  Globe,
  HardDrive,
  Star,
  Palette,
  Megaphone,
  Mail,
  Award,
  Wrench,
  BarChart3,
  Gift,
  Download,
  LogOut,
  Monitor,
  Smartphone,
  Tablet,
  Building2,
  CreditCard,
  Bell,
  Server,
  CalendarDays,
  Briefcase,
  Handshake,
  Send,
  AlertTriangle,
  MessageSquareHeart,
  Sparkles,
  ClipboardList,
  Bug,
  Hash
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import { APP_VERSION, formatVersionDate } from '../../config/version';

// =============================================
// SIDEBAR V1 - ESPACE CLIENT MSC (Simplifié)
// =============================================
// Navigation uniquement pour la V1
// Accès ADMIN et DEVELOP sont des pages séparées
// =============================================

const sectionColors = {
  production: { bg: 'bg-emerald-500/10', text: 'text-emerald-300', accent: '#10b981' },
  commerce: { bg: 'bg-sky-500/10', text: 'text-sky-300', accent: '#0ea5e9' },
  finance: { bg: 'bg-amber-500/10', text: 'text-amber-300', accent: '#f59e0b' },
  team: { bg: 'bg-indigo-500/10', text: 'text-indigo-300', accent: '#6366f1' },
  partage: { bg: 'bg-pink-500/10', text: 'text-pink-300', accent: '#ec4899' },
  support: { bg: 'bg-violet-500/10', text: 'text-violet-300', accent: '#8b5cf6' },
  projects: { bg: 'bg-emerald-500/10', text: 'text-emerald-300', accent: '#10b981' },
  admin: { bg: 'bg-red-500/10', text: 'text-red-300', accent: '#ef4444' },
};

const Sidebar = ({ collapsed, setCollapsed, mobileOpen, setMobileOpen }) => {
  const { user, logout, isAgency, isProjectManager, isTeamMember, isClient, isCustomerService, isAffiliate } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Ref for the navigation scroll area
  const navScrollRef = useRef(null);

  const [expandedSections, setExpandedSections] = useState({
    production: true,
    commerce: true,
    finance: true,
    team: true,
    partage: true,
    support: true,
    projects: true,
    admin: true
  });

  // Client-specific: Check if client has payment schedules
  const [hasPaymentSchedules, setHasPaymentSchedules] = useState(true);
  
  // Unread messages count for badge
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Fetch unread messages count
  const fetchUnreadCount = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      let total = 0;
      
      if (isClient) {
        // Client: check client-chat and project messages
        const [clientChat, projectMsgs] = await Promise.all([
          api.get('/client-chat/conversations').catch(() => ({ data: [] })),
          api.get('/projects/').catch(() => ({ data: [] }))
        ]);
        
        total = (clientChat.data || []).reduce((sum, c) => sum + (c.unread_count || 0), 0);
        // For projects, would need a separate unread endpoint
      } else if (isAgency || isProjectManager || isCustomerService || isTeamMember) {
        // Agency: check internal chat, client chat, and project messages
        const [internal, clientConvs] = await Promise.all([
          api.get('/internal-chat/conversations').catch(() => ({ data: [] })),
          api.get('/client-chat/conversations').catch(() => ({ data: [] }))
        ]);
        
        total = (internal.data || []).reduce((sum, c) => sum + (c.unread_count || 0), 0);
        total += (clientConvs.data || []).reduce((sum, c) => sum + (c.unread_count || 0), 0);
      }
      
      setUnreadCount(total);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, [user?.id, isClient, isAgency, isProjectManager, isCustomerService, isTeamMember]);
  
  // Fetch unread count on mount and periodically
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000); // Every 30 seconds
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);
  
  // Save scroll position before navigation
  const saveScrollPosition = useCallback(() => {
    if (navScrollRef.current) {
      sessionStorage.setItem('sidebarScrollPos', navScrollRef.current.scrollTop.toString());
    }
  }, []);
  
  // Restore scroll position after navigation
  useEffect(() => {
    const savedPos = sessionStorage.getItem('sidebarScrollPos');
    if (savedPos && navScrollRef.current) {
      // Small delay to ensure DOM is ready
      requestAnimationFrame(() => {
        if (navScrollRef.current) {
          navScrollRef.current.scrollTop = parseInt(savedPos, 10);
        }
      });
    }
  }, [location.pathname]);

  useEffect(() => {
    // Only check for clients
    if (isClient && user?.id) {
      api.get('/client/payment-schedules')
        .then(res => {
          const schedules = res.data || [];
          setHasPaymentSchedules(schedules.length > 0);
        })
        .catch(() => {
          setHasPaymentSchedules(false);
        });
    }
  }, [isClient, user?.id]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleInstallApp = () => {
    if (window.deferredPrompt) {
      window.deferredPrompt.prompt();
    } else {
      window.dispatchEvent(new CustomEvent('show-pwa-modal'));
    }
  };

  // =============================================
  // NAVIGATION AGENCE V2 - Réorganisée et épurée
  // =============================================
  const agencyNavigation = [
    { to: '/dashboard/agency', icon: LayoutDashboard, label: 'Accueil' },
    { to: '/calendar', icon: Calendar, label: 'Agenda' },
    { to: '/chat', icon: MessageSquare, label: 'Messagerie', highlighted: true },
    
    {
      section: 'production',
      label: 'Production',
      icon: FolderKanban,
      items: [
        { to: '/projects', icon: FolderKanban, label: 'Projets' },
        { to: '/planning', icon: Calendar, label: 'Planning' },
        { to: '/tickets', icon: MessageSquare, label: 'Tickets' },
        { to: '/domains', icon: Globe, label: 'Domaines' },
      ]
    },
    
    {
      section: 'commerce',
      label: 'Commerce',
      icon: Users,
      items: [
        { to: '/prospects', icon: UserPlus, label: 'Prospects' },
        { to: '/clients', icon: Users, label: 'Clients' },
        { to: '/contracts', icon: FileSignature, label: 'Forfaits' },
        { to: '/affiliates', icon: Handshake, label: 'Apporteurs' },
      ]
    },
    
    {
      section: 'finance',
      label: 'Finance',
      icon: Euro,
      items: [
        { to: '/clients-wallets', icon: Wallet, label: 'Wallets Clients' },
        { to: '/wallet-management', icon: Building2, label: 'Virements' },
        { to: '/receipts/pending', icon: Receipt, label: 'Reçus' },
        { to: '/service-invoices', icon: Euro, label: 'Facturation' },
        { to: '/payment-schedules', icon: Calendar, label: 'Échéanciers' },
        { to: '/admin/recovery', icon: AlertTriangle, label: 'Recouvrement' },
        { to: '/hourly-rates', icon: Clock, label: 'Tarification' },
      ]
    },
    
    {
      section: 'team',
      label: 'Équipe',
      icon: Users,
      items: [
        { to: '/users', icon: User, label: 'Membres' },
        { to: '/team/subcontractors', icon: Briefcase, label: 'Sous-traitants' },
        { to: '/monthly-reports', icon: FileText, label: 'Rapports' },
        { to: '/payment-requests', icon: Wallet, label: 'Règlements' },
        { to: '/member-contracts', icon: FileSignature, label: 'Contrats' },
        { to: '/ratings', icon: Star, label: 'Évaluations' },
      ]
    },
    
    {
      section: 'partage',
      label: 'Ressources',
      icon: HardDrive,
      items: [
        { to: '/knowledge', icon: BookOpen, label: 'Base de connaissances' },
        { to: '/shared-supports', icon: Image, label: 'Supports de com\'' },
        { to: '/shared-passwords', icon: Key, label: 'Accès' },
        { to: '/documentation', icon: Code, label: 'Documentation technique' },
        { to: '/saved-links', icon: Bookmark, label: 'Liens' },
      ]
    },
    
    {
      section: 'admin',
      label: 'Administration',
      icon: Settings,
      items: [
        { to: '/settings/agency-info', icon: Building2, label: 'Infos Agence' },
        { to: '/settings/nomenclature', icon: Hash, label: 'Nomenclatures' },
        { to: '/admin/theme', icon: Palette, label: 'Apparence' },
        { to: '/agency/brief-templates', icon: FileSignature, label: 'Templates Brief' },
        { to: '/contract-templates', icon: FileText, label: 'Modèles Contrats' },
        { to: '/admin/embed-forms', icon: Code, label: 'Formulaires Embed' },
        { to: '/admin/announcements', icon: Megaphone, label: 'Annonces' },
        { to: '/admin/roadmap', icon: Bug, label: 'Bugs & Roadmap' },
        { to: '/admin/settings', icon: Server, label: 'Paramètres Système' },
      ]
    },
  ];

  // =============================================
  // NAVIGATION CLIENT V2 - Simplifiée
  // =============================================
  const clientNavigation = [
    { to: '/dashboard/client', icon: LayoutDashboard, label: 'Accueil' },
    { to: '/chat', icon: MessageCircle, label: 'Messages', highlighted: true },
    { to: '/my-appointments', icon: Calendar, label: 'Mes RDV' },
    
    {
      section: 'production',
      label: 'Production',
      icon: FolderKanban,
      items: [
        { to: '/my-projects', icon: FolderKanban, label: 'Projets' },
        { to: '/client-forfaits', icon: Wrench, label: 'Forfaits' },
        { to: '/domains', icon: Globe, label: 'Domaines' },
      ]
    },
    
    {
      section: 'finance',
      label: 'Finances',
      icon: Wallet,
      items: [
        { to: '/wallet', icon: Wallet, label: 'Wallet' },
        { to: '/my-invoices', icon: Receipt, label: 'Factures' },
        { to: '/my-schedules', icon: CalendarDays, label: 'Échéanciers' },
        { to: '/my-contracts', icon: FileSignature, label: 'Contrats' },
      ]
    },
    
    {
      section: 'partage',
      label: 'Ressources',
      icon: HardDrive,
      items: [
        { to: '/knowledge', icon: BookOpen, label: 'Base de connaissances' },
        { to: '/shared-supports', icon: Image, label: 'Supports de com\'' },
        { to: '/shared-passwords', icon: Key, label: 'Accès' },
      ]
    },
    
    { to: '/tickets', icon: MessageSquare, label: 'Support' },
    { to: '/help', icon: BookOpen, label: 'Aide' },
  ];

  // =============================================
  // NAVIGATION MEMBRE ÉQUIPE V2 - Épurée
  // =============================================
  const creativeNavigation = [
    { to: '/dashboard/team', icon: LayoutDashboard, label: 'Accueil' },
    { to: '/chat', icon: MessageSquare, label: 'Messagerie', highlighted: true },
    { to: '/planning', icon: Calendar, label: 'Planning' },
    { to: '/my-projects', icon: FolderKanban, label: 'Mes projets' },
    { to: '/my-schedule', icon: Clock, label: 'Mes horaires' },
    { to: '/my-reports', icon: FileText, label: 'Rapports' },
    { to: '/my-member-contracts', icon: FileSignature, label: 'Contrats' },
    { to: '/knowledge', icon: BookOpen, label: 'Tutoriels' },
    { to: '/saved-links', icon: Bookmark, label: 'Liens' },
  ];

  // =============================================
  // NAVIGATION SERVICE CLIENT V2 - Épurée
  // =============================================
  const customerServiceNavigation = [
    { to: '/dashboard/customer-service', icon: LayoutDashboard, label: 'Accueil' },
    { to: '/calendar', icon: Calendar, label: 'Agenda' },
    { to: '/chat', icon: MessageSquare, label: 'Messagerie', highlighted: true },
    
    {
      section: 'production',
      label: 'Production',
      icon: FolderKanban,
      items: [
        { to: '/projects', icon: FolderKanban, label: 'Projets' },
        { to: '/planning', icon: Calendar, label: 'Planning' },
        { to: '/tickets', icon: MessageSquare, label: 'Tickets' },
      ]
    },
    
    {
      section: 'commerce',
      label: 'Commerce',
      icon: Users,
      items: [
        { to: '/prospects', icon: UserPlus, label: 'Prospects' },
        { to: '/clients', icon: Users, label: 'Clients' },
        { to: '/contracts', icon: FileSignature, label: 'Contrats' },
        { to: '/admin/announcements', icon: Megaphone, label: 'Annonces' },
      ]
    },
    
    {
      section: 'finance',
      label: 'Finance',
      icon: Euro,
      items: [
        { to: '/receipts/pending', icon: Receipt, label: 'Reçus' },
        { to: '/service-invoices', icon: Euro, label: 'Facturation' },
        { to: '/payment-requests', icon: Wallet, label: 'Paiements' },
        { to: '/ratings', icon: Star, label: 'Évaluations' },
      ]
    },
    
    {
      section: 'team',
      label: 'Équipe',
      icon: Users,
      items: [
        { to: '/users', icon: User, label: 'Membres' },
        { to: '/my-schedule', icon: Clock, label: 'Mes Horaires' },
      ]
    },
    
    {
      section: 'partage',
      label: 'Ressources',
      icon: HardDrive,
      items: [
        { to: '/shared-supports', icon: Image, label: 'Supports de com\'' },
        { to: '/shared-passwords', icon: Key, label: 'Accès' },
        { to: '/documentation', icon: BookOpen, label: 'Documentation' },
      ]
    },
  ];

  // =============================================
  // NAVIGATION APPORTEUR V2
  // =============================================
  const affiliateNavigation = [
    { to: '/affiliate/dashboard', icon: LayoutDashboard, label: 'Accueil' },
    { to: '/affiliate/leads', icon: Users, label: 'Mes leads' },
    { to: '/affiliate/commissions', icon: Euro, label: 'Commissions' },
    { to: '/affiliate/onboarding', icon: FileText, label: 'Mon profil' },
    { to: '/affiliate/contract', icon: FileSignature, label: 'Mon contrat' },
  ];

  // Sélection de la navigation selon le rôle
  const getNavigation = () => {
    if (isClient) {
      // Filter client navigation based on conditions
      return clientNavigation.map(item => {
        if (item.section === 'finance') {
          return {
            ...item,
            items: item.items.filter(subItem => {
              // Hide "Échéanciers" if client has no schedules
              if (subItem.to === '/my-schedules' && !hasPaymentSchedules) {
                return false;
              }
              return true;
            })
          };
        }
        return item;
      });
    }
    if (isAffiliate) return affiliateNavigation;
    if (isCustomerService) return customerServiceNavigation;
    if (isTeamMember && !isProjectManager) return creativeNavigation;
    if (isProjectManager) return agencyNavigation; // Chef de projet a accès à la navigation agence
    return agencyNavigation;
  };

  const navigation = getNavigation();

  const renderNavItem = (item, sectionKey = null) => {
    const Icon = item.icon;
    const colors = sectionKey ? sectionColors[sectionKey] : null;
    const isMessaging = item.to === '/chat' || item.label === 'Messagerie' || item.label === 'Messages';
    
    // Generate data-tour attribute based on label
    const getTourId = (label) => {
      const mapping = {
        'Tableau de bord': 'dashboard',
        'Projets': 'projets',
        'Mes Projets': 'projets',
        'Tâches': 'taches',
        'Mes Tâches': 'taches',
        'Planning': 'planning',
        'Mon Planning': 'planning',
        'Agenda': 'planning',
        'Messagerie': 'messages',
        'Messages': 'messages',
        'Clients': 'clients',
        'Équipe': 'equipe',
        'Finances': 'finances',
        'Facturation': 'facturation',
        'Paramètres': 'parametres',
        'Rendez-vous': 'rdv',
        'Mes Rendez-vous': 'rdv',
        'Leads': 'leads',
        'Mes Leads': 'leads',
        'Commissions': 'commissions',
        'Mes Commissions': 'commissions',
        'Base de connaissances': 'ressources',
        'Tutoriels': 'ressources',
      };
      return mapping[label] || label.toLowerCase().replace(/\s+/g, '-');
    };
    
    return (
      <NavLink
        key={item.to}
        to={item.to}
        onClick={saveScrollPosition}
        data-tour={`sidebar-item-${getTourId(item.label)}`}
        className={({ isActive }) =>
          cn(
            "flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-sm",
            collapsed ? "justify-center" : "",
            item.highlighted && "bg-sky-100 ml-2 mr-1 shadow-sm border-l-4 border-sky-400",
            isActive
              ? item.highlighted 
                ? "bg-sky-200 text-sky-900 font-bold"
                : colors 
                  ? `${colors.bg} ${colors.text} font-medium`
                  : "bg-white/10 text-white font-medium"
              : item.highlighted
                ? "text-sky-800 hover:bg-sky-200"
                : "text-white/60 hover:text-white hover:bg-white/5"
          )
        }
      >
        <div className="relative">
          <Icon className="w-4 h-4 flex-shrink-0" />
          {/* Unread badge for messaging */}
          {isMessaging && unreadCount > 0 && collapsed && (
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
          )}
        </div>
        {!collapsed && (
          <div className="flex items-center justify-between flex-1">
            <span>{item.label}</span>
            {isMessaging && unreadCount > 0 && (
              <Badge className="bg-red-500 text-white text-[10px] px-1.5 py-0 h-4 min-w-[18px] flex items-center justify-center">
                {unreadCount > 99 ? '99+' : unreadCount}
              </Badge>
            )}
          </div>
        )}
      </NavLink>
    );
  };

  const renderSection = (section) => {
    const Icon = section.icon;
    const colors = sectionColors[section.section];
    const isExpanded = expandedSections[section.section];
    
    // Generate data-tour attribute for sections
    const getSectionTourId = (label) => {
      const mapping = {
        'Ressources': 'ressources',
        'Équipe': 'equipe',
        'Finances': 'finances',
        'Administration': 'admin',
      };
      return mapping[label] || label.toLowerCase().replace(/\s+/g, '-');
    };
    
    return (
      <div key={section.section} className="mb-2">
        <button
          onClick={() => toggleSection(section.section)}
          data-tour={`sidebar-item-${getSectionTourId(section.label)}`}
          className={cn(
            "w-full flex items-center justify-between px-3 py-2 rounded-lg transition-all",
            "text-white/70 hover:text-white hover:bg-white/5",
            collapsed && "justify-center"
          )}
        >
          <div className="flex items-center gap-3">
            <Icon className="w-4 h-4" style={{ color: colors?.accent }} />
            {!collapsed && <span className="text-sm font-medium">{section.label}</span>}
          </div>
          {!collapsed && (
            <ChevronDown className={cn(
              "w-4 h-4 transition-transform",
              isExpanded && "rotate-180"
            )} />
          )}
        </button>
        
        {!collapsed && isExpanded && (
          <div className="ml-4 mt-1 space-y-1 border-l border-white/10 pl-3">
            {section.items.map(item => renderNavItem(item, section.section))}
          </div>
        )}
      </div>
    );
  };

  // Helper to get role label
  const getRoleLabel = () => {
    if (isClient) return { label: 'Client', color: 'bg-blue-500/20 text-blue-300' };
    if (isAffiliate) return { label: 'Apporteur', color: 'bg-pink-500/20 text-pink-300' };
    if (isCustomerService) return { label: 'Service Client', color: 'bg-purple-500/20 text-purple-300' };
    if (isTeamMember && !isProjectManager) return { label: 'Équipe', color: 'bg-emerald-500/20 text-emerald-300' };
    if (isProjectManager) return { label: 'Chef de Projet', color: 'bg-indigo-500/20 text-indigo-300' };
    if (isAgency) return { label: 'Administrateur', color: 'bg-red-500/20 text-red-300' };
    return { label: 'Utilisateur', color: 'bg-slate-500/20 text-slate-300' };
  };

  const roleInfo = getRoleLabel();

  const sidebarContent = (
    <>
      {/* Header with Large Circular Logo and Role Badge */}
      <div className="p-4 border-b border-white/10">
        <div className={cn("flex items-center gap-3", collapsed && "justify-center")}>
          {/* Large Circular Logo with Subtle Energy Beam Effect */}
          <div 
            className={cn(
              "relative flex-shrink-0",
              collapsed ? "w-16 h-16" : "w-[72px] h-[72px]"
            )}
            data-testid="sidebar-agency-logo"
          >
            {/* Subtle rotating beam - thin clockwise */}
            <div 
              className="absolute inset-[-2px] rounded-full"
              style={{
                background: `conic-gradient(from 0deg, transparent 0%, transparent 85%, ${theme.colors?.accent || '#00d4aa'} 92%, #38bdf8 96%, transparent 100%)`,
                animation: 'spin-clockwise 3s linear infinite',
              }}
            />
            {/* Inner logo container */}
            <div 
              className="absolute inset-[2px] rounded-full flex items-center justify-center overflow-hidden z-10"
              style={{ 
                backgroundColor: theme.colors?.primary || '#1a365d',
                boxShadow: `0 0 15px ${theme.colors?.accent || '#00d4aa'}30, inset 0 0 10px rgba(0,0,0,0.3)`,
              }}
            >
              {theme.logo?.darkUrl ? (
                <img 
                  src={theme.logo.darkUrl} 
                  alt="Logo" 
                  className="object-cover w-full h-full"
                />
              ) : theme.logo?.lightUrl ? (
                <img 
                  src={theme.logo.lightUrl} 
                  alt="Logo" 
                  className="object-cover w-full h-full"
                />
              ) : (
                <div className="flex items-center justify-center w-full h-full">
                  <span className={cn(
                    "font-black text-white tracking-tight",
                    collapsed ? "text-lg" : "text-xl"
                  )}>
                    {theme.agencyName?.substring(0, 3)?.toUpperCase() || 'MSC'}
                  </span>
                </div>
              )}
            </div>
          </div>
          {!collapsed && (
            <div>
              <h1 className="text-white font-bold text-lg">
                Manager
              </h1>
              {/* Role Badge */}
              <Badge 
                className={cn("text-[10px] font-medium px-2 py-0 h-5 mt-0.5", roleInfo.color)}
                data-testid="user-role-badge"
              >
                {roleInfo.label}
              </Badge>
              {/* Version info - discrete */}
              {(isAgency || isProjectManager || isCustomerService) && (
                <button
                  onClick={() => navigate('/changelog')}
                  className="block mt-1 text-[10px] text-white/40 hover:text-white/70 transition-colors cursor-pointer text-left"
                  title="Voir le changelog"
                >
                  <span className="block">v{APP_VERSION.version}</span>
                  <span className="block text-[9px] opacity-70">{formatVersionDate(APP_VERSION.buildDate)}</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav ref={navScrollRef} className="flex-1 p-3 overflow-y-auto min-h-0" data-tour="sidebar">
        <div className="space-y-1 pb-4">
          {navigation.map((item) => (
            item.section ? renderSection(item) : renderNavItem(item)
          ))}
        </div>
      </nav>

      {/* Footer - Install App & Logout */}
      <div className="p-3 border-t border-white/10 space-y-2">
        {/* Install App Button */}
        {!collapsed ? (
          <button
            onClick={handleInstallApp}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-all group"
            data-testid="sidebar-install-app"
          >
            <Download className="w-4 h-4 flex-shrink-0" />
            <div className="flex-1 text-left">
              <span className="text-xs font-medium block">Installer l&apos;app</span>
              <span className="text-[10px] text-white/40 flex items-center gap-1">
                <Monitor className="w-3 h-3" />
                <Smartphone className="w-3 h-3" />
                <Tablet className="w-3 h-3" />
                PC, Mobile, Tablette
              </span>
            </div>
          </button>
        ) : (
          <button
            onClick={handleInstallApp}
            className="w-full flex items-center justify-center p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-all"
            title="Installer l'app"
            data-testid="sidebar-install-app"
          >
            <Download className="w-4 h-4" />
          </button>
        )}

        {/* Logout Button */}
        {!collapsed ? (
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-red-400/70 hover:text-red-400 hover:bg-red-500/10 transition-all"
            data-testid="sidebar-logout"
          >
            <LogOut className="w-4 h-4 flex-shrink-0" />
            <span className="text-xs font-medium">Déconnexion</span>
          </button>
        ) : (
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center p-2 rounded-lg text-red-400/70 hover:text-red-400 hover:bg-red-500/10 transition-all"
            title="Déconnexion"
            data-testid="sidebar-logout"
          >
            <LogOut className="w-4 h-4" />
          </button>
        )}
      </div>
    </>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside
        className={cn(
          "fixed top-0 left-0 h-full z-40 transition-all duration-300 hidden md:flex flex-col",
          collapsed ? "w-20" : "w-64"
        )}
        style={{ backgroundColor: theme.colors?.primary || '#1a365d' }}
      >
        {sidebarContent}
      </aside>

      {/* Mobile Sidebar */}
      {mobileOpen && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setMobileOpen(false)}
          />
          <aside
            className="fixed top-0 left-0 h-full w-64 z-50 flex flex-col md:hidden"
            style={{ backgroundColor: theme.colors?.primary || '#1a365d' }}
          >
            {sidebarContent}
          </aside>
        </>
      )}
    </>
  );
};

export default Sidebar;
